import { useCallback } from 'react';
import { AxiosResponse } from 'axios';

import { createRestaurantsService } from '@restworld/data-services';
import { JobPosition, Period, PlacesInfoResponseType, Restaurant } from '@restworld/utility-types';
import { ShowRestaurant } from '@restworld/data-services';

import { RestaurantJobPositionsProps } from '../components/restaurant-job-positions';

export const baseUrl = process.env.NEXT_PUBLIC_BASE_API_URL;

export const getJobPositionsPerDomain = (jobPositions: JobPosition[], domain: string) => {
  return (
    jobPositions
      ?.filter((jobPosition) => jobPosition?.domain_interest_var?.label?.toLowerCase()?.includes(domain))
      ?.map((job) => ({
        id: job?.id || null,
        showRestaurantDetails: true,
        restaurantName: job?.restaurant?.restaurant_name || '',
        city: job?.restaurant?.locality || null,
        jobTitle: job?.job_title || null,
        occupation: job?.occupation?.label_it?.label || null,
        salaryMin: job?.min_salary || null,
        salaryMax: job?.max_salary || null,
        jobType: job?.job_type_var?.label || null,
        salary: job?.salary || null,
        restaurantLogo: job?.restaurant?.restaurant_logo || null,
        publishedAt: job?.published_at || null,
        redirectUrl: job?.semantic_unique_name ? `/posizione/${job?.semantic_unique_name}` : '#!',
        staffRequested: job?.staff_requested || 0,
      })) || []
  );
};

export const minifyRestaurantData = (restaurant: Restaurant) => {
  const showRestaurantDetails = restaurant?.show_details || restaurant?.show_details === true ? true : false;
  const jobPositions = (restaurant?.public_restaurant_active_job_positions || []).map((job) => ({
    ...job,
    restaurant: {
      ...job.restaurant,
      show_details: showRestaurantDetails,
      restaurant_name: restaurant?.restaurant_name,
      locality: restaurant?.locality,
      restaurant_logo: restaurant?.restaurant_logo,
    },
  }));
  const activeJobPositions = getDomainsJobPositions(jobPositions);
  const googleMapsURL = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurant.address)}`;

  return {
    pictures: restaurant?.pictures?.map((picture) => picture?.url)?.filter((url) => url) || [],
    restaurantName: restaurant?.restaurant_name || null,
    organization: {
      id: restaurant?.organization?.id || null,
      organization_name: restaurant?.organization?.name || null,
    },
    covers: restaurant?.covers_per_service_var?.label || null,
    staffSize: (restaurant?.staff_size_floor || 0) + (restaurant?.staff_size_kitchen || 0),
    activeJobPositions,
    mapLocation: {
      approximateLat: restaurant?.approximate_lat || null,
      approximateLon: restaurant?.approximate_lon || null,
      lat: restaurant?.address_lat || null,
      lon: restaurant?.address_lon || null,
      locationDescription: restaurant?.location_description || null,
      showRestaurant: true,
      address: restaurant?.address || null,
      locality: restaurant?.locality || null,
      googleMapsURL,
    },
    briefDescription: restaurant?.brief_description || null,
    // Carried through verbatim, with no `||` and no default: `undefined` has to
    // stay `undefined`. The flag decides `noindex` on the showcase, and the two
    // falsy values mean opposite things there. See `hasOwnContent` on
    // `RestaurantStaticType`.
    hasOwnContent: restaurant?.has_own_content,
    restaurantId: restaurant?.id || null,
    showRestaurantDetails,
    insertedAt: restaurant?.inserted_at || null,
    restaurantLogo: restaurant?.restaurant_logo || null,
    socialMediaUrl: {
      ...(restaurant?.['the_fork_url'] ? { the_fork_url: restaurant?.['the_fork_url'], label: 'The  Fork' } : {}),
      ...(restaurant?.['linkedin_url'] ? { linkedin_url: restaurant?.['linkedin_url'] } : {}),
      ...(restaurant?.['instagram_url'] ? { instagram_url: restaurant?.['instagram_url'] } : {}),
      ...(restaurant?.['other_social_media_url']
        ? { other_social_media_url: restaurant?.['other_social_media_url'] }
        : {}),
      ...(restaurant?.['tripadvisor_url'] ? { tripadvisor_url: restaurant?.['tripadvisor_url'] } : {}),
      ...(restaurant?.['facebook_url'] ? { facebook_url: restaurant?.['facebook_url'] } : {}),
      ...(restaurant?.['tiktok_url'] ? { tiktok_url: restaurant?.['tiktok_url'] } : {}),
      ...(restaurant?.['website_url'] ? { website_url: restaurant?.['website_url'] } : {}),
    },
    shifts: restaurant?.showcase_shifts?.map((v) => v.showcase_shift_var.label) || [],
    benefits: restaurant?.restaurant_benefits?.map((v) => v.restaurant_benefit_var.label) || [],
    otherBenefits: restaurant?.other_benefits || null,
    values: restaurant?.showcase_values ?? [],
    venueOwnerData: {
      ownerName: restaurant?.owner_name || '',
      ownerProfilePicture: restaurant?.owner_profile_picture || '',
      ownerMessage: restaurant?.owner_message || '',
    },
    averageHiresPerYear: restaurant?.average_hires_per_year_var?.key || null,
    ...(restaurant?.occupation_templates
      ? {
          occupationTemplates: restaurant.occupation_templates
            .map(({ title, occupation }) => occupation?.label_it?.label ?? title)
            .filter(Boolean),
        }
      : {}),
    restaurantEmployerName: restaurant?.employer?.user?.name || null,
    restaurantOrganizationName: restaurant?.employer?.business_name || null,
  };
};

export const minifyRestaurantPlacesInfo = (restaurantPlacesInfo: PlacesInfoResponseType) => {
  return {
    openingHours: restaurantPlacesInfo?.opening_hours?.periods || [],
    rating: restaurantPlacesInfo?.rating || null,
    userRatingsTotal: restaurantPlacesInfo?.user_ratings_total || 0,
    reviews: restaurantPlacesInfo?.reviews || [],
    totalReviews: restaurantPlacesInfo?.user_ratings_total || 0,
  };
};

export const fetchRestaurantInfoInternal = async (
  slug: string,
  fetchRestaurantFn: (slug: string) => Promise<AxiosResponse<ShowRestaurant>>
): Promise<RestaurantStaticType> => {
  const restaurantStaticResponse = await fetchRestaurantFn(slug);
  const restaurantStatic = restaurantStaticResponse?.data?.restaurant;

  const restaurantPlacesResponse = await createRestaurantsService({ baseUrl }).restaurantPlacesInfo(
    restaurantStatic?.id
  );

  const minifiedRestaurantData = minifyRestaurantData(restaurantStatic as Restaurant);

  const minifiedRestaurantPlacesData = minifyRestaurantPlacesInfo(
    restaurantPlacesResponse?.data as PlacesInfoResponseType
  );

  return {
    ...minifiedRestaurantData,
    ...minifiedRestaurantPlacesData,
    slug,
    metaTitle: restaurantStatic?.meta_title,
    metaDescription: restaurantStatic?.meta_description,
    metaImage: restaurantStatic?.meta_image,
  };
};

export function useFetchRestaurantInformation() {
  const fetchRestaurantInformation = useCallback(async (slug: string) => {
    const service = createRestaurantsService({ baseUrl });
    const fetchFn = (slug: string) => service.showPublicRestaurant(slug);

    return fetchRestaurantInfoInternal(slug, fetchFn);
  }, []);

  return { fetchRestaurantInformation };
}

export const fetchRestaurantInformation = async (slug: string): Promise<RestaurantStaticType> => {
  const service = createRestaurantsService({ baseUrl });

  const fetchFn = (slug: string) => service.showPublicRestaurant(slug);

  return fetchRestaurantInfoInternal(slug, fetchFn);
};

export const getDomainsJobPositions = (jobPositions: JobPosition[]) => {
  const domains = ['reception', 'sala', 'cucina', 'bar'];
  const domainsJobPositions = [];
  domains.forEach((domain) => {
    const jobPositionsPerDomain = getJobPositionsPerDomain(jobPositions, domain);
    if (jobPositionsPerDomain?.length > 0) {
      domainsJobPositions.push({
        domain,
        label: domain === 'reception' ? 'hotellerie' : domain,
        jobs: jobPositionsPerDomain || [],
      });
    }
  });
  return domainsJobPositions;
};

export type RestaurantStaticType = {
  restaurantId: string;
  restaurantLogo?: string;
  slug: string;
  pictures: string[];
  restaurantName: string;
  organization: {
    id: string;
    organization_name: string;
  };
  covers: string;
  staffSize: number;
  activeJobPositions: RestaurantJobPositionsProps['jobPositionsPerDomain'];
  mapLocation: {
    approximateLat: number;
    approximateLon: number;
    lat: number;
    lon: number;
    locationDescription: string;
    showRestaurant: boolean;
    address: string;
    locality?: string;
    googleMapsURL?: string;
  };
  // `minifyRestaurantData` maps an empty `brief_description` to `null`, and the
  // declared `string` is what let that `null` reach a template literal.
  briefDescription: string | null;
  /**
   * Whether the showcase has text of its own, straight from the backend, which
   * is the only place the rule is written (`OwnContent` in core-services). The
   * sitemap in `Restworld/website` reads the same flag off the ids endpoint, so
   * a showcase cannot be declared there and `noindex` here.
   *
   * Three states, not two, and that is the point:
   *   `true`      the page has something to rank, index it
   *   `false`     the page is empty, `noindex` it
   *   `undefined` the backend did not say, so keep today's behaviour and index
   *
   * `undefined` happens for a response older than the backend release. Treating
   * it as `false` would `noindex` every showcase at once.
   */
  hasOwnContent?: boolean;
  openingHours: Period[];
  rating: number;
  totalReviews: number;
  showRestaurantDetails?: boolean;
  insertedAt?: string;
  metaTitle?: string;
  metaDescription?: string;
  metaImage?: string;
  socialMediaUrl?: {
    the_fork_url?: string;
    linkedin_url?: string;
    instagram_url?: string;
    other_social_media_url?: string;
    tripadvisor_url?: string;
    facebook_url?: string;
    tiktok_url?: string;
    website_url?: string;
  };
  shifts?: string[];
  benefits?: string[];
  otherBenefits?: string;
  values?: string[];
  venueOwnerData?: {
    ownerName?: string;
    ownerProfilePicture?: string;
    ownerMessage?: string;
  };
  occupationTemplates?: string[];
  averageHiresPerYear?: string;
  restaurantEmployerName?: string;
  restaurantOrganizationName?: string;
};
